import { c as _c } from "react/compiler-runtime";
import { Box, Flex, Separator, Stack } from "@chakra-ui/react";
import { defineMessages, useIntl } from "react-intl";
import { ColorToggle } from "../ColorToggle";
import { FilterContainer } from "../FilterContainer";
import { ViewModeButtons } from "./ViewModeButtons";
import { ViewMode, ToggleMetric, TOGGLE_METRICS } from "@/utils/metricUtils";
import { RangeFilterInput } from "@/components/ui/filters/metrics/RangeFilterInput";
import { system } from "@/styles/theme";
import { Metric, NumericRangeDto } from "@/api/generated/models";
export type RangeMetric = Extract<Metric, "speed" | "acceleration" | "density">;
export interface RangeValues {
  speed: NumericRangeDto;
  acceleration: NumericRangeDto;
  density: NumericRangeDto;
}
export interface MetricFilterProps {
  selectedMetric: ToggleMetric;
  onMetricChange: (metric: ToggleMetric) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  rangeValues: RangeValues | null;
  onRangeChange: (metric: RangeMetric, range: {
    min: number;
    max: number;
  }) => void;
  datasetRanges: RangeValues | null;
}
const messages = defineMessages({
  metricSpeed: {
    id: "metric.speed",
    defaultMessage: "Speed"
  },
  metricAcceleration: {
    id: "metric.acceleration",
    defaultMessage: "Acceleration"
  },
  metricDensity: {
    id: "metric.density",
    defaultMessage: "Density"
  },
  metricVehicleType: {
    id: "metric.vehicleType",
    defaultMessage: "Vehicle Types"
  }
});
const metricMessageKeys: Record<ToggleMetric, keyof typeof messages> = {
  [Metric.speed]: "metricSpeed",
  [Metric.acceleration]: "metricAcceleration",
  [Metric.density]: "metricDensity",
  [Metric.vehicleType]: "metricVehicleType"
};
export const MetricFilter = t0 => {
  const $ = _c(26);
  const {
    selectedMetric,
    onMetricChange,
    viewMode,
    onViewModeChange,
    rangeValues,
    onRangeChange,
    datasetRanges
  } = t0;
  const intl = useIntl();
  let t1;
  if ($[0] !== onMetricChange || $[1] !== selectedMetric) {
    t1 = metric => {
      if (metric === selectedMetric) {
        return;
      }
      onMetricChange(metric);
    };
    $[0] = onMetricChange;
    $[1] = selectedMetric;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleToggle = t1;
  let t2;
  if ($[3] !== handleToggle || $[4] !== intl || $[5] !== selectedMetric) {
    t2 = TOGGLE_METRICS.map(metric_0 => <ColorToggle key={metric_0} color={system.token("colors.mobilysisGreen")} checked={selectedMetric === metric_0} onToggle={() => handleToggle(metric_0)} label={intl.formatMessage(messages[metricMessageKeys[metric_0]])} labelPosition="end" data-testid={`metric-toggle-${metric_0}`} />);
    $[3] = handleToggle;
    $[4] = intl;
    $[5] = selectedMetric;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <Stack direction="row" gap="12" justify="center">{t2}</Stack>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Separator borderColor="grey.200" marginTop="8" marginBottom="8" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = selectedMetric === Metric.speed || selectedMetric === Metric.acceleration ? "flex-start" : "center";
  let t6;
  if ($[10] !== datasetRanges || $[11] !== onRangeChange || $[12] !== rangeValues || $[13] !== selectedMetric) {
    t6 = rangeValues && datasetRanges && (selectedMetric === Metric.speed || selectedMetric === Metric.acceleration) && <Box data-testid="range-filter-section" flex="1"><RangeFilterInput metric={selectedMetric} datasetMin={datasetRanges[selectedMetric].min} datasetMax={datasetRanges[selectedMetric].max} currentMin={rangeValues[selectedMetric].min} currentMax={rangeValues[selectedMetric].max} onRangeChange={range => onRangeChange(selectedMetric, range)} /></Box>;
    $[10] = datasetRanges;
    $[11] = onRangeChange;
    $[12] = rangeValues;
    $[13] = selectedMetric;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onViewModeChange || $[16] !== selectedMetric || $[17] !== viewMode) {
    t7 = <ViewModeButtons selectedMetric={selectedMetric} viewMode={viewMode} onViewModeChange={onViewModeChange} />;
    $[15] = onViewModeChange;
    $[16] = selectedMetric;
    $[17] = viewMode;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <Flex align="center" gap="4" wrap="wrap" justify={t5}>{t6}{t7}</Flex>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t3 || $[24] !== t8) {
    t9 = <FilterContainer data-testid="metric-toggle-card" maxWidth="800px">{t3}{t4}{t8}</FilterContainer>;
    $[23] = t3;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};